import {
  Anchor,
  Button,
  Checkbox,
  Image,
  Text,
  TextInput,
  Title,
  useComputedColorScheme,
} from '@mantine/core';
import { IconMail } from '@tabler/icons-react';
import { useMutation } from '@tanstack/react-query';
import axios, { isAxiosError } from 'axios';
import { CSSProperties, FormEvent, useEffect, useRef, useState } from 'react';
import tokens from '../../../design/tokens.json';
import {
  getPendingLoginHint,
  hasConsentedToTerms,
  Issuer,
  isFreeIssuerEnabled,
  recordTermsConsent,
  setPendingLoginHint,
  storeIssuer,
  takeSkipSessionProbe,
} from '@/lib/auth';
import { logger } from '@/lib/logger';
import { claimSessionProbe, probeSession } from '@/lib/sessionProbe';
import { FullScreenLoading } from './FullScreenLoading';

const { color } = tokens.shared;
const tint = (hex: string, percent: number) =>
  `color-mix(in srgb, ${hex} ${percent}%, ${color.surface})`;

// PLaMo翻訳のログイン画面（translate.preferredai.jp の plamo-mesh）の背景を shared.color で組み直したもの。
// 青と紫は blue / violet scale、赤はロゴ色を白に寄せる。
const plamoMesh = (
  [
    [15, 30, color.scale.blue[0], 40],
    [75, 10, color.scale.violet[1], 35],
    [25, 70, tint(color.logo.red, 6), 45],
    [90, 40, color.scale.blue[2], 30],
    [5, 85, color.scale.violet[2], 40],
    [60, 80, tint(color.logo.red, 12), 35],
    [85, 75, color.scale.blue[1], 25],
    [35, 5, color.scale.violet[0], 45],
    [10, 10, color.scale.blue[1], 40],
    [65, 45, tint(color.logo.red, 6), 30],
    [45, 90, color.scale.violet[1], 35],
    [95, 20, color.scale.blue[0], 40],
  ] as const
)
  .map(([x, y, c, stop]) => `radial-gradient(at ${x}% ${y}%, ${c} 0px, transparent ${stop}%)`)
  .join(', ');

const TERMS_URL = 'https://plamo.preferredai.jp/info/terms';
const PRIVACY_POLICY_URL = 'https://www.preferred.jp/ja/policy/';

type Props = {
  onIssuerResolved: (issuer: Issuer) => void;
};

export function LoginScreen({ onIssuerResolved }: Props) {
  const [email, setEmail] = useState(() => getPendingLoginHint() ?? '');
  const [hasAgreed, setHasAgreed] = useState(hasConsentedToTerms);
  // A pending hint resolves the issuer without the form; otherwise a silent
  // session probe runs (loading) and the form appears only if it finds no session.
  // Without recorded consent both are skipped so the checkbox is never bypassed.
  const [probing, setProbing] = useState(
    () => isFreeIssuerEnabled() && hasConsentedToTerms() && getPendingLoginHint() === null
  );
  const probeStarted = useRef(false);
  const scheme = useComputedColorScheme('light');

  const loginRoute = useMutation({
    mutationFn: async (email: string) => {
      const res = await axios.post<{ is_paid_tenant: unknown }>(
        new URL('/api/signup/v1/login-route', import.meta.env.VITE_API_GATEWAY_URL).toString(),
        { email }
      );
      // 判定を読めない応答で無料側へ流さない
      if (typeof res.data.is_paid_tenant !== 'boolean') {
        throw new Error('unexpected login-route response');
      }
      return { data: { is_paid_tenant: res.data.is_paid_tenant } };
    },
    onSuccess: ({ data }, email) => {
      const issuer = data.is_paid_tenant ? 'paid' : 'free';
      storeIssuer(issuer);
      setPendingLoginHint(email);
      onIssuerResolved(issuer);
    },
    // The error holds the request body, so only the status is logged to keep the email out.
    onError: (error) =>
      logger.error('login-route failed:', isAxiosError(error) ? error.response?.status : error),
  });

  useEffect(() => {
    const pending = getPendingLoginHint();
    if (pending && hasConsentedToTerms()) {
      // The skip flag is spent here too: left behind, it would skip a later probe.
      takeSkipSessionProbe();
      loginRoute.mutate(pending);
    }
  }, []);

  // Before the form, silently probe (paid then free) for an existing Auth0 session
  // so an already-signed-in user lands in the app with no screen or redirect. The
  // explicit-logout flag skips it once so a deliberate logout of one tenant is not
  // undone by the other tenant's still-alive session. The started guard stops a
  // StrictMode double effect from opening two iframe flows for the same client.
  useEffect(() => {
    if (!probing || probeStarted.current) return;
    probeStarted.current = true;
    const isSkipped = takeSkipSessionProbe();
    if (isSkipped || !claimSessionProbe()) {
      setProbing(false);
      return;
    }
    void probeSession([
      {
        issuer: 'paid',
        domain: import.meta.env.VITE_AUTH0_DOMAIN ?? '',
        clientId: import.meta.env.VITE_AUTH0_CLIENT_ID ?? '',
      },
      {
        issuer: 'free',
        domain: import.meta.env.VITE_AUTH0_FREE_DOMAIN ?? '',
        clientId: import.meta.env.VITE_AUTH0_FREE_CLIENT_ID ?? '',
      },
    ]).then((issuer) => {
      if (issuer) {
        // The same storage the email screen writes: the provider that mounts next
        // reads back the cached session and finds the user already signed in.
        storeIssuer(issuer);
        onIssuerResolved(issuer);
      } else {
        setProbing(false);
      }
    });
  }, [probing, onIssuerResolved]);

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    recordTermsConsent();
    loginRoute.mutate(email);
  };

  const isRateLimited = isAxiosError(loginRoute.error) && loginRoute.error.response?.status === 429;

  if (probing) {
    return <FullScreenLoading />;
  }

  return (
    // 100vh は iOS Safari ではツールバーを含まない高さなので、その分だけカードが
    // 見えている範囲の中心より下に出る。dvh は実際に見えている高さに追従する
    <div
      className="flex min-h-dvh items-center justify-center p-4 [background-image:var(--plamo-mesh)]"
      style={
        {
          // メッシュは白に寄せた淡色で組んであるので dark では敷かない
          '--plamo-mesh': scheme === 'dark' ? 'none' : plamoMesh,
          backgroundColor: 'var(--plamo-page-bg)',
        } as CSSProperties
      }
    >
      <form
        onSubmit={handleSubmit}
        className="w-full max-w-[460px] p-6 md:p-10 [border-radius:var(--mantine-radius-lg)] [box-shadow:var(--mantine-shadow-md)]"
        style={{ backgroundColor: 'var(--mantine-color-body)' }}
      >
        <Image src="/plm_a_full_rgb.png" h={32} fit="contain" alt="PLaMo" />
        <Title order={1} fz="lg" ta="center" mt={40} lts="0.05em">
          会員登録 / ログイン
        </Title>
        <Text ta="center" c="dimmed" size="sm" mt="md" className="whitespace-pre-line">
          {'メールアドレスでログインできます。\nはじめての方も、そのまま登録できます。'}
        </Text>
        <TextInput
          mt={40}
          size="md"
          type="email"
          label="メールアドレス"
          placeholder="email@example.com"
          leftSection={<IconMail size={16} />}
          labelProps={{ fw: 700, fz: 'sm', mb: 12 }}
          radius="sm"
          styles={{ input: { height: 48 } }}
          required
          withAsterisk={false}
          value={email}
          onChange={(e) => setEmail(e.currentTarget.value)}
        />
        {/* 寸法・色は chat_ui のログイン画面にそろえる */}
        <Checkbox
          mt={24}
          size="xs"
          radius="xs"
          styles={{ label: { color: 'var(--mantine-color-dimmed)' } }}
          checked={hasAgreed}
          onChange={(e) => setHasAgreed(e.currentTarget.checked)}
          label={
            <>
              <Anchor
                href={TERMS_URL}
                target="_blank"
                rel="noreferrer"
                inherit
                c="inherit"
                underline="always"
              >
                利用規約
              </Anchor>
              と
              <Anchor
                href={PRIVACY_POLICY_URL}
                target="_blank"
                rel="noreferrer"
                inherit
                c="inherit"
                underline="always"
              >
                プライバシーポリシー
              </Anchor>
              に同意し、日本国内の居住者または法人かつ18歳以上であることを確認しました。
            </>
          }
        />
        <Button
          type="submit"
          h={44}
          radius="sm"
          fw={700}
          fullWidth
          mt={24}
          loading={loginRoute.isPending}
          disabled={!hasAgreed}
          // 押せない間も主色のまま薄くする（Mantine の既定は灰色の面）
          className="disabled:!bg-[var(--mantine-primary-color-filled)] disabled:!text-[var(--mantine-primary-color-contrast)] disabled:opacity-50"
        >
          続ける
        </Button>
        {loginRoute.isError && (
          <Text c="red" size="sm" mt={24}>
            {isRateLimited
              ? 'しばらく待ってから再度お試しください。'
              : 'ログインに失敗しました。時間をおいて再度お試しください。'}
          </Text>
        )}
      </form>
    </div>
  );
}
